import React, { useEffect, useRef, useState } from "react";
import { DocumentIcon, ArchiveBoxIcon, ArrowUpTrayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "@/shadcn/hooks/use-toast";

const getFileIcon = (type, name) => {
    if (type.startsWith("image/")) return null; // Images will show preview
    if (type === "application/pdf") return <DocumentIcon className="w-8 h-8 text-red-500" />;
    if (type === "application/zip" || name.endsWith(".zip")) return <ArchiveBoxIcon className="w-8 h-8 text-yellow-500" />;
    if (name.match(/\.(doc|docx)$/)) return <DocumentIcon className="w-8 h-8 text-blue-500" />;
    return <DocumentIcon className="w-8 h-8 text-gray-400" />;
};

const MAX_SIZE = 10 * 1024 * 1024; // 10MB

export default function FileUploader({ files, onFileSelect }) {
    const fileInputRef = useRef<HTMLInputElement>();

    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            if (e.clipboardData && e.clipboardData.files.length > 0) {
                const imageFiles = Array.from(e.clipboardData.files).filter((file: File) => file.type.startsWith("image/"));
                if (imageFiles.length > 0) {
                    // Combine with existing files if needed
                    onFileSelect([...files, ...imageFiles]);
                }
            }
        };

        // Attach to the uploader div or document
        document.addEventListener("paste", handlePaste);

        return () => {
            document.removeEventListener("paste", handlePaste);
        };
    }, [files, onFileSelect]);

    const removeFile = (idx: number) => {
        const newFiles = files.slice();
        newFiles.splice(idx, 1);
        onFileSelect(newFiles);
    };

    const handleFiles = (fileList) => {
        // Filter out files over 10MB
        const validFiles = Array.from(fileList).filter((file: File) => file.size <= MAX_SIZE);
        const oversized = Array.from(fileList).filter((file: File) => file.size > MAX_SIZE);
        if (oversized.length) {
            toast({
                title: "File Size Limit Exceeded",
                description: `Some files are too large (max 10MB): ${oversized.map((f: File) => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)} MB)`).join(", ")}`,
                variant: "destructive",
            });
        }
        onFileSelect(validFiles);
    };

    const onInputChange = (e) => {
        handleFiles(e.target.files);
    };

    const onDrop = (e) => {
        e.preventDefault();
        handleFiles(e.dataTransfer.files);
    };

    const onDragOver = (e) => {
        e.preventDefault();
    };

    return (
        <div className="">
            <div className="flex items-center gap-2 mb-2 max-w-64">
                <div
                    className="flex flex-col items-center p-5 transition bg-white border border-gray-300 border-dashed rounded-lg cursor-pointer hover:bg-gray-100"
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                    <ArrowUpTrayIcon className="w-8 h-8 text-gray-400" />
                    <input type="file" multiple className="hidden" ref={fileInputRef} onChange={onInputChange} />
                </div>
                <div className="flex flex-col gap-2">
                    <span className="text-xs text-gray-600 opacity-40">Drag & drop files here or paste an image</span>
                    <div>
                        <button
                            type="button"
                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                            className="inline-flex justify-center px-2 py-1 text-xs transition bg-white border border-gray-200 rounded-md shadow-sm hover:text-white hover:bg-blue-700 focus:outline-none">
                            Select Files
                        </button>
                    </div>
                </div>
            </div>
            {/* Preview List */}
            <div className="flex flex-wrap items-center gap-2 text-gray-600">
                {files.map((file, idx) => (
                    <div key={file.name + idx} className="inline-flex items-center gap-2 p-2 border rounded bg-gray-50">
                        {file.type.startsWith("image/") ? (
                            <img src={URL.createObjectURL(file)} alt={file.name} className="object-cover w-12 h-12 rounded" />
                        ) : (
                            getFileIcon(file.type, file.name)
                        )}
                        <div className="flex-1">
                            <div title={file.name} className="text-sm font-medium text-gray-800 truncate max-w-40">
                                {file.name}
                            </div>
                            <div className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</div>
                            <div className="text-xs text-gray-400">{file.type}</div>
                        </div>
                        <button type="button" className="p-1 text-gray-400 hover:text-red-600" title="Remove" onClick={() => removeFile(idx)}>
                            <XMarkIcon className="w-5 h-5" />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
